import { useState } from 'react'
import {
  Badge,
  Button,
  Combobox,
  Input,
  Modal,
  Row,
  Stack,
  Textarea,
  errorToast,
  spacing,
  toast,
} from '@/components/ui'
import { $api, $client } from '@/lib/fetch'
import { prettyTimestamp } from '@/lib/helpers'
import { EventTypeType } from '@/lib/types'

const PARSER_TYPES = [
  { label: 'Instagram', value: 'instagram' },
  { label: 'JanazaApp', value: 'janazaapp' },
]

const PARSER_CONFIG_HINTS: Record<string, string> = {
  instagram: '{"page":"nom_de_la_page"}',
  janazaapp: '{}',
}

const DEFAULT_EVENT_TYPE_OPTIONS = [
  { value: '', label: '— Aucun —' },
  ...Object.values(EventTypeType).map((v) => ({ value: v, label: v })),
]

interface ScraperFormState {
  id: string
  period_ms: string
  parser_type: string
  parser_config: string
  default_address: string
  default_event_type: string
  entity_id: string
}

const EMPTY_FORM: ScraperFormState = {
  id: '',
  period_ms: '86400000',
  parser_type: 'instagram',
  parser_config: '{"page":""}',
  default_address: '',
  default_event_type: '',
  entity_id: '',
}

interface ScraperLike {
  id: string
  period_ms: number
  parser_type: string
  parser_config: string
  default_address?: string
  default_event_type?: string
  entity_id?: number
  paused: boolean
  last_successful_fetch_ms: number
  last_fetch_ms: number
  fetched_events: number
  new_events: number
}

function scraperToForm(s: ScraperLike): ScraperFormState {
  return {
    id: s.id,
    period_ms: String(s.period_ms),
    parser_type: s.parser_type,
    parser_config: s.parser_config,
    default_address: s.default_address ?? '',
    default_event_type: s.default_event_type ?? '',
    entity_id: s.entity_id != null ? String(s.entity_id) : '',
  }
}

export default function AdminScrapersCRUD() {
  const { data, refetch, isLoading } = $api.useQuery('get', '/api/scrapers')
  const { data: entitiesData } = $api.useQuery('get', '/api/entities')
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null)
  const [editing, setEditing] = useState<ScraperLike | null>(null)
  const [form, setForm] = useState<ScraperFormState>(EMPTY_FORM)
  const [busy, setBusy] = useState(false)

  const entityOptions = [
    { value: '', label: '— Aucune entité —' },
    ...(entitiesData?.entities ?? []).map(
      (e: { id: number; name: string }) => ({
        value: String(e.id),
        label: e.name,
      }),
    ),
  ]

  const openCreate = () => {
    setForm(EMPTY_FORM)
    setEditing(null)
    setModalMode('create')
  }
  const openEdit = (s: ScraperLike) => {
    setForm(scraperToForm(s))
    setEditing(s)
    setModalMode('edit')
  }
  const closeModal = () => {
    setModalMode(null)
    setEditing(null)
  }

  const submit = async () => {
    setBusy(true)
    const body = {
      period_ms: Number(form.period_ms),
      parser_type: form.parser_type,
      parser_config: form.parser_config,
      default_address: form.default_address || undefined,
      default_event_type: form.default_event_type || undefined,
      entity_id: form.entity_id ? Number(form.entity_id) : undefined,
    }
    try {
      if (modalMode === 'create') {
        const resp = await $client.POST('/api/scrapers', {
          body: { id: form.id, ...body },
        })
        if (resp.error) {
          errorToast('Erreur à la création', resp.error)
          return
        }
        toast({ color: 'success', title: 'Scraper créé' })
      } else {
        const resp = await $client.PATCH('/api/scrapers/{id}', {
          params: { path: { id: editing!.id } },
          body,
        })
        if (resp.error) {
          errorToast('Erreur à la mise à jour', resp.error)
          return
        }
        toast({ color: 'success', title: 'Scraper mis à jour' })
      }
      closeModal()
      refetch()
    } finally {
      setBusy(false)
    }
  }

  const deleteScraper = async (id: string) => {
    if (!confirm(`Supprimer le scraper "${id}" ?`)) return
    const resp = await $client.DELETE('/api/scrapers/{id}', {
      params: { path: { id } },
    })
    if (resp.error) errorToast('Erreur à la suppression', resp.error)
    else {
      toast({ color: 'success', title: 'Scraper supprimé' })
      refetch()
    }
  }

  const togglePause = async (id: string, paused: boolean) => {
    await $client.POST('/api/scrapers/{id}/status', {
      params: { path: { id } },
      body: { paused },
    })
    refetch()
  }

  const runScraper = async (id: string) => {
    const resp = await $client.POST('/api/scrapers/run', {
      body: { ids: [id] },
    })
    if (resp.error) errorToast('Impossible de lancer', resp.error)
    else {
      toast({ color: 'success', title: 'Lancé !' })
      refetch()
    }
  }

  if (isLoading) return <p style={{ color: 'var(--ink-2)' }}>Chargement…</p>

  return (
    <>
      <Row justify="flex-end" style={{ marginBottom: spacing.lg }}>
        <Button variant="primary" onClick={openCreate}>
          + Nouveau scraper
        </Button>
      </Row>
      <div className="oumma-table-wrap">
        <table className="oumma-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>Période</th>
              <th>Récupérés</th>
              <th>Nouveaux</th>
              <th>Dernier succès</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(data?.scrapers ?? []).map((s) => (
              <tr key={s.id}>
                <td>
                  <code
                    style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}
                  >
                    {s.id}
                  </code>
                </td>
                <td>{s.parser_type}</td>
                <td>{Math.round(s.period_ms / 60000)}min</td>
                <td>{s.fetched_events}</td>
                <td>{s.new_events}</td>
                <td>{prettyTimestamp(s.last_successful_fetch_ms)}</td>
                <td>
                  <Badge tone={s.paused ? 'terracotta' : 'emerald'}>
                    {s.paused ? 'Pausé' : 'Actif'}
                  </Badge>
                </td>
                <td>
                  <Row gap="sm" style={{ flexWrap: 'wrap' }}>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => runScraper(s.id)}
                    >
                      Lancer
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => togglePause(s.id, !s.paused)}
                    >
                      {s.paused ? 'Reprendre' : 'Pause'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(s)}
                    >
                      Éditer
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      style={{ color: 'var(--danger)' }}
                      onClick={() => deleteScraper(s.id)}
                    >
                      Supprimer
                    </Button>
                  </Row>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalMode !== null}
        onClose={closeModal}
        title={
          modalMode === 'create' ? 'Nouveau scraper' : `Éditer ${editing?.id}`
        }
      >
        <Stack gap="lg">
          {modalMode === 'create' && (
            <Input
              label="ID"
              value={form.id}
              onChange={(e) => setForm({ ...form, id: e.target.value })}
              placeholder="nom:type:v0"
            />
          )}
          <Input
            label="Période (ms)"
            type="number"
            value={form.period_ms}
            onChange={(e) => setForm({ ...form, period_ms: e.target.value })}
            min={60000}
          />
          <Combobox
            label="Type de parser"
            value={form.parser_type}
            options={PARSER_TYPES}
            onChange={(v) => {
              setForm({
                ...form,
                parser_type: v,
                parser_config: PARSER_CONFIG_HINTS[v] ?? '{}',
              })
            }}
          />
          <Textarea
            label={`Config parser (JSON) — ex: ${PARSER_CONFIG_HINTS[form.parser_type] ?? '{}'}`}
            value={form.parser_config}
            onChange={(e) =>
              setForm({ ...form, parser_config: e.target.value })
            }
            rows={3}
          />
          <Input
            label="Adresse par défaut (optionnel)"
            value={form.default_address}
            onChange={(e) =>
              setForm({ ...form, default_address: e.target.value })
            }
          />
          <Combobox
            label="Type d'événement par défaut (optionnel)"
            value={form.default_event_type}
            options={DEFAULT_EVENT_TYPE_OPTIONS}
            onChange={(v) => setForm({ ...form, default_event_type: v })}
          />
          <Combobox
            label="Entité liée (optionnel)"
            value={form.entity_id}
            options={entityOptions}
            onChange={(v) => setForm({ ...form, entity_id: v })}
          />
          <Row gap="sm" justify="flex-end">
            <Button variant="secondary" onClick={closeModal}>
              Annuler
            </Button>
            <Button variant="primary" loading={busy} onClick={submit}>
              {modalMode === 'create' ? 'Créer' : 'Enregistrer'}
            </Button>
          </Row>
        </Stack>
      </Modal>
    </>
  )
}
